import { Switch, Route, useParams, Redirect } from 'react-router'
import { useCallback, useMemo } from 'react'
import { unnest } from 'ramda'

import { createPath } from '../routePaths'
import { ProjectPageLayout } from '../_common/ProjectPageLayout'
import { WarehousePageNavigation } from './WarehousePageNavigation'
import * as stock from './pages/WarehouseStockPage'
import * as outbound from './pages/WarehouseOutboundPage'
import * as inbound from './pages/WarehouseInboundPage'
import * as orders from './pages/WarehouseOrdersPage'
import * as intransit from './pages/WarehouseInTransitPage'
import * as movements from './pages/WarehouseStockMovementsPage'
import * as settings from './pages/WarehouseSettingsPage'

export const defaultSection = stock.section

const warehouseSectionTranslate = {
  [stock.section]: 'Остатки',
  [outbound.section]: 'Отгрузки',
  [inbound.section]: 'Приходы',
  [orders.section]: 'Заказы',
  [intransit.section]: 'Продукция в пути',
  [movements.section]: 'Движение продукции',
  [settings.section]: 'Настройки',
}

const translateWarehouseSection = (section: string) => warehouseSectionTranslate[section]

export const WarehousePageSection = ({ baseUrl }: { baseUrl: string }) => {
  const pathMask = `${baseUrl}/:section`

  const createWarehousePath = useCallback(
    (section?: string) => createPath({ section }, pathMask),
    [pathMask],
  )

  const mask = useCallback(
    (section: string, msk?: string) => {
      return msk ? `${createWarehousePath(section)}${msk}` : createWarehousePath(section)
    },
    [createWarehousePath],
  )

  const params = useParams()

  const sections = useMemo(
    () => [
      stock.section,
      outbound.section,
      inbound.section,
      orders.section,
      intransit.section,
      movements.section,
      settings.section,
    ],
    [],
  )

  const routes = useMemo(() => {
    return [stock, outbound, inbound, orders, intransit, movements, settings].map(
      ({ section, Component, masks }) => {
        const baseUrl = mask(section)
        return {
          section,
          Component,
          baseUrl,
          otherUrls: masks.map((msk) => mask(section, msk)),
        }
      },
    )
  }, [mask])

  return (
    <ProjectPageLayout header="Склад">
      <WarehousePageNavigation
        sections={sections}
        activeSection={params.section}
        onCreatePath={createWarehousePath}
        onTranslateSection={translateWarehouseSection}
      />
      <Switch>
        {unnest(
          routes.map(({ section, baseUrl, otherUrls, Component }) => {
            return [
              <Route
                key={section}
                path={baseUrl}
                render={() => <Component baseUrl={baseUrl} siblings={routes} />}
                exact
              />,
              ...otherUrls.map((path) => (
                <Route
                  key={path}
                  path={path}
                  render={() => <Component baseUrl={baseUrl} siblings={routes} />}
                  exact
                />
              )),
            ]
          }),
        )}
        <Redirect to={createWarehousePath(defaultSection)} />
      </Switch>
    </ProjectPageLayout>
  )
}
